// Unless explicitly stated otherwise all files in this repository are licensed under the MIT License.
// This product includes software developed at Datadog (https://www.datadoghq.com/).
// Copyright 2019-Present Datadog, Inc.

import { getChunkDebugId } from '@dd/core/helpers/debugId';
import type { ChunkInfo } from '@dd/core/types';
import { randomUUID } from 'crypto';

import type { SourceCodeContextOptions } from './types';

export const DEFAULT_SOURCE_CODE_CONTEXT_VARIABLE = 'DD_SOURCE_CODE_CONTEXT' as const;

// The source code context snippet - single injection with function definition and call
// SSR-safe: checks window before accessing, never throws
//
// Capture only this injected function, whose first frame identifies the bundle URL.
// Capturing callers can trigger expensive V8 source-position collection for the entire bundle.
// Restore the application's setting before formatting the stack (which may invoke user code).
//
// Unminified version:
// (function(c, n) {
//     try {
//         if (typeof window === 'undefined') return;
//         var w = window, m = w[n] = w[n] || {}, l = Error.stackTraceLimit;
//         try {
//             Error.stackTraceLimit = 1;
//             var e = new Error();
//         } finally {
//             Error.stackTraceLimit = l;
//         }
//         var s = e.stack;
//         s && (m[s] = c);
//     } catch (e) {}
// })(context, variableName);

type SourceCodeContext = {
    service?: string;
    version?: string;
    ddDebugId?: string;
};

export type SourceCodeContextSnippet = {
    code: string;
    debugId?: string;
};

export const getSourceCodeContextSnippet = (
    contextOptions: SourceCodeContextOptions,
    chunk?: ChunkInfo,
): SourceCodeContextSnippet => {
    // Compute deterministic debug IDs whenever possible to prevent the backend from storing
    // duplicate source maps for identical builds.
    let debugId: string | undefined;
    if (contextOptions.debugId === true) {
        debugId = chunk ? getChunkDebugId(chunk) : randomUUID();
    }
    const context: SourceCodeContext = {
        service: contextOptions.service,
        version: contextOptions.version,
        // The `dd` prefix lets upload tools locate the value and send it as sourcemap metadata.
        ddDebugId: debugId,
    };

    const code = `(function(c,n){try{if(typeof window==='undefined')return;var w=window,m=w[n]=w[n]||{},l=Error.stackTraceLimit;try{Error.stackTraceLimit=1;var e=new Error()}finally{Error.stackTraceLimit=l}var s=e.stack;s&&(m[s]=c)}catch(e){}})(${JSON.stringify(context)},${JSON.stringify(DEFAULT_SOURCE_CODE_CONTEXT_VARIABLE)});`;

    return { code, debugId };
};
